/**
 * WordPress dependencies
 */
import {
	Button,
	Dashicon,
	Modal,
	SelectControl,
} from '@safe-wordpress/components';
import { useDispatch, useSelect } from '@safe-wordpress/data';
import {
	createInterpolateElement,
	useEffect,
	useState,
} from '@safe-wordpress/element';
import { _x, sprintf } from '@safe-wordpress/i18n';
import { addQueryArgs } from '@safe-wordpress/url';

/**
 * External dependencies
 */
import clsx from 'clsx';
import { capitalize, orderBy, range } from 'lodash';
import { store as NAB_DATA, usePageAttribute } from '@nab/data';
import { store as NAB_EXPERIMENTS } from '@nab/experiments';
import type {
	AiCandidateSearchState,
	AiHypothesisSelectionState,
	AiSettings,
	AiSiteAnalysisState,
	AiTestCandidate,
	AiTestCreationFromHypothesisState,
	AiTestGenerationDoneState,
	AiTestHypothesis,
	Maybe,
} from '@nab/types';

/**
 * External dependencies
 */
import { hasHead } from '@nab/utils';

/**
 * Internal dependencies
 */
import './style.scss';
import { Confetti } from './confetti';
import { AiGoogleAnalyticsData } from '../../../ai-google-analytics-data';
import { AiIcon } from '../../../ai-icon';
import { AiPrivacySettings } from '../../../ai-privacy-settings';
import { LoadingAnimation } from '../../../loading-animation';
import { PremiumFeature } from '../../../premium-feature';
import { Preview } from '../../../preview';

export type AiGenerateTestModalProps = {
	readonly aiSettings: Extract< AiSettings, { type: 'setup' | 'ready' } >;
};

export const AiGenerateTestModal = ( {
	aiSettings,
}: AiGenerateTestModalProps ): JSX.Element | null => {
	const [ isLoadingPage, setIsLoadingPage ] = useState( false );
	const [ state, setState ] = usePageAttribute(
		'ai/testGenerationState',
		false
	);

	if ( ! state ) {
		return null;
	}

	const isDismissible = ! isLoadingPage && 'done' !== state.type;
	const close = () => isDismissible && setState( false );

	return (
		<Modal
			className="nab-ai-test-generation-modal"
			title={ ( <Title /> ) as unknown as string }
			isDismissible={ isDismissible }
			shouldCloseOnEsc={ isDismissible }
			shouldCloseOnClickOutside={ false }
			onRequestClose={ close }
		>
			<ModalContent
				aiSettings={ aiSettings }
				isLoadingPage={ isLoadingPage }
				markAsLoading={ () => setIsLoadingPage( true ) }
			/>
		</Modal>
	);
};

// ============
// HELPER VIEWS
// ============

const Title = () => (
	<>
		<AiIcon gap="6px" />
		{ sprintf(
			'Nelio AI – %s',
			_x( 'Test Generation', 'text', 'nelio-ab-testing' )
		) }
	</>
);

type LoadingPageProps = {
	readonly aiSettings: Extract< AiSettings, { type: 'setup' | 'ready' } >;
	readonly isLoadingPage: boolean;
	readonly markAsLoading: () => void;
};
const ModalContent = ( props: LoadingPageProps ): JSX.Element | null => {
	const [ state, setState ] = usePageAttribute(
		'ai/testGenerationState',
		false
	);

	if ( ! state ) {
		return null;
	}

	switch ( state.type ) {
		case 'intro':
			return <IntroContent aiSettings={ props.aiSettings } />;

		case 'analyzing-site':
		case 'searching-candidates':
		case 'creating-test':
			return <ProcessingContent state={ state } />;
		case 'hypothesis-selection':
			return (
				<HypothesisSelectionContent
					state={ state }
					setState={ setState }
				/>
			);
		case 'done':
			return <DoneContent state={ state } { ...props } />;
		case 'error':
			return <ErrorContent message={ getErrorMessage( state.reason ) } />;
	}
};

const IntroContent = ( {
	aiSettings,
}: {
	readonly aiSettings: Extract< AiSettings, { type: 'setup' | 'ready' } >;
} ) => {
	const types = useExperimentTypes();
	const [ privacySettings, doSetPrivacySettings ] = useState(
		aiSettings.privacy
	);
	const setPrivacySettings = ( attrs: Partial< typeof privacySettings > ) =>
		doSetPrivacySettings( { ...privacySettings, ...attrs } );

	const [ analyticsSettings, doSetAnalyticsSettings ] = useState(
		aiSettings.analytics
	);
	const setAnalyticsSettings = (
		attrs: Partial< typeof analyticsSettings >
	) => doSetAnalyticsSettings( { ...analyticsSettings, ...attrs } );

	const { searchTestCandidates } = useDispatch( NAB_DATA );
	// Auto-start if we already have the proper settings defined.
	useEffect(
		() =>
			void (
				'ready' === aiSettings.type && searchTestCandidates( types )
			)
	);

	const [ _, setState ] = usePageAttribute( 'ai/testGenerationState', false );
	const close = () => setState( false );

	return (
		<div className="nab-ai-test-generation-modal__intro-content">
			<h2>
				{ _x(
					'Meet Nelio AI: Smarter Testing, Your Way',
					'text',
					'nelio-ab-testing'
				) }
			</h2>
			<p className="nab-ai-test-generation-modal__intro-content-description">
				{ _x(
					'Nelio AI is a new feature powered by advanced language models that helps you identify high-impact testing opportunities and streamlines the creation of A/B tests in your WordPress site. By analyzing your content, Nelio AI can suggest what to test next and, thus, help you make smarter, data-driven decisions faster.',
					'text',
					'nelio-ab-testing'
				) }
			</p>
			<p className="nab-ai-test-generation-modal__intro-content-description">
				{ createInterpolateElement(
					_x(
						'Because your content is your business, <strong>your privacy and control come first</strong>. Before making any suggestions, Nelio AI needs access to certain content on your site. Below, you can review and customize exactly what information is shared with the AI, so it works for you—on your terms.',
						'text',
						'nelio-ab-testing'
					),
					{ strong: <strong /> }
				) }
			</p>
			<p className="nab-ai-test-generation-modal__intro-content-description">
				{ createInterpolateElement(
					_x(
						'You can <strong>change these preferences at any time</strong> by going to <strong>Nelio A/B Testing » Settings</strong>.',
						'text',
						'nelio-ab-testing'
					),
					{ strong: <strong /> }
				) }
			</p>
			<div className="nab-ai-test-generation-modal__intro-content-settings">
				<h2>
					{ _x( 'Privacy Settings', 'text', 'nelio-ab-testing' ) }
				</h2>
				<AiPrivacySettings
					attributes={ privacySettings }
					setAttributes={ setPrivacySettings }
				/>
				<AiGoogleAnalyticsData
					attributes={ analyticsSettings }
					setAttributes={ setAnalyticsSettings }
				/>
			</div>
			<div className="nab-ai-test-generation-modal__intro-content-actions">
				<Button variant="secondary" onClick={ close }>
					{ _x( 'Close', 'command', 'nelio-ab-testing' ) }
				</Button>
				<Button
					variant="primary"
					onClick={ () =>
						searchTestCandidates( types, {
							...aiSettings,
							privacy: privacySettings,
							analytics: analyticsSettings,
						} )
					}
				>
					{ _x( 'Start', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</div>
	);
};

const ProcessingContent = ( {
	state,
}: {
	readonly state:
		| AiSiteAnalysisState
		| AiCandidateSearchState
		| AiTestCreationFromHypothesisState;
} ) => {
	const getMessage = (): string => {
		if ( 'creating-test' === state.type ) {
			return _x(
				'Setting up your new A/B test…',
				'text',
				'nelio-ab-testing'
			);
		}

		if ( 'analyzing-site' === state.type ) {
			switch ( state.what ) {
				case 'saving-ai-settings':
					return _x(
						'Saving your privacy settings…',
						'text',
						'nelio-ab-testing'
					);

				case 'previous-candidates':
					return _x(
						'Initializing site analysis…',
						'text',
						'nelio-ab-testing'
					);

				case 'blog':
					return _x(
						'Analyzing your blog content…',
						'text',
						'nelio-ab-testing'
					);

				case 'pages':
					return _x(
						'Reviewing your website pages…',
						'text',
						'nelio-ab-testing'
					);

				case 'ecommerce':
					return _x(
						'Analyzing product performance and e-commerce metrics…',
						'text',
						'nelio-ab-testing'
					);

				case 'ab-testing':
					return _x(
						'Reviewing results from previous A/B tests…',
						'text',
						'nelio-ab-testing'
					);

				case 'custom-posts':
					return _x(
						'Processing additional content…',
						'text',
						'nelio-ab-testing'
					);

				case 'google-analytics':
					return _x(
						'Fetching data from Google Analytics…',
						'text',
						'nelio-ab-testing'
					);

				case 'first-hypotheses':
					return _x(
						'Generating test ideas…',
						'text',
						'nelio-ab-testing'
					);
			}
		}

		// searching-candidates
		switch ( state.step ) {
			case 'just-started':
				return _x(
					'Identifying the best opportunities for optimization…',
					'text',
					'nelio-ab-testing'
				);

			case 'keep-waiting':
				return _x(
					'Hang tight—finding the most impactful tests takes a moment…',
					'user',
					'nelio-ab-testing'
				);

			case 'quite-slow':
				return _x( 'Almost there…', 'text', 'nelio-ab-testing' );
		}
	};

	return (
		<div className="nab-ai-test-generation-modal__loading">
			<LoadingAnimation />
			<p className="nab-ai-test-generation-modal__loading-message">
				{ getMessage() }
			</p>
		</div>
	);
};

const HypothesisSelectionContent = ( {
	state,
	setState,
}: {
	readonly state: AiHypothesisSelectionState;
	readonly setState: ( s: AiHypothesisSelectionState ) => void;
} ) => {
	const { siteAnalysis, candidates: simpleCandidates } = state;

	const candidates = simpleCandidates
		.map( ( c ): Maybe< AiTestCandidate > => {
			const item = siteAnalysis.content[ c.type ]?.find(
				( i ) => i.id === c.id
			);
			return item
				? ( {
						...c,
						url: item.url,
				  } as AiTestCandidate )
				: undefined;
		} )
		.filter( ( x ) => !! x );

	const update = ( nc: AiTestCandidate ) =>
		setState( {
			...state,
			candidates: state.candidates.map( ( oc ) =>
				oc.id === nc.id && oc.type === nc.type ? nc : oc
			),
		} );

	if ( ! hasHead( candidates ) ) {
		const message = sprintf(
			'%1$s %2$s',
			_x(
				'Nelio AI was unable to identify any elements for testing.',
				'text',
				'nelio-ab-testing'
			),
			_x( 'Please try again later.', 'user', 'nelio-ab-testing' )
		);
		return <ErrorContent message={ message } />;
	}

	const [ firstCandidate, ...otherCandidates ] = candidates;
	return (
		<div className="nab-ai-test-generation-modal__hypothesis-selection-content">
			<h3>
				{ _x(
					'Best Opportunity for Optimization',
					'text',
					'nelio-ab-testing'
				) }
			</h3>
			<Candidate candidate={ firstCandidate } update={ update } />
			{ otherCandidates.length && (
				<>
					<h3>
						{ _x(
							'Worth Considering Next',
							'text',
							'nelio-ab-testing'
						) }
					</h3>
					{ otherCandidates.map( ( candidate ) => (
						<Candidate
							key={ `nab-candidate-${ candidate.type }-${ candidate.id }` }
							candidate={ candidate }
							update={ update }
						/>
					) ) }
				</>
			) }
		</div>
	);
};

const Candidate = ( {
	candidate,
	update,
}: {
	readonly candidate: AiTestCandidate;
	readonly update: ( c: AiTestCandidate ) => void;
} ) => {
	const getTypeName = useTypeNameGetter();
	return (
		<div
			key={ `nab-candidate-${ candidate.type }-${ candidate.id }` }
			className="nab-ai-test-generation-modal__candidate"
		>
			<div className="nab-ai-test-generation-modal__candidate-info">
				<div className="nab-ai-test-generation-modal__candidate-preview">
					<Preview
						url={ addQueryArgs( candidate.url, {
							'nab-ai-preview': 1,
						} ) }
					/>
				</div>
				<div className="nab-ai-test-generation-modal__candidate-content">
					<div
						className={ `nab-ai-test-generation-modal__candidate-type nab-ai-test-generation-modal__candidate-type--is-${ candidate.type }` }
					>
						{ getTypeName( candidate.type ) }
					</div>
					<div className="nab-ai-test-generation-modal__candidate-priority">
						{ range( getStars( candidate.priority ).fullStars ).map(
							( i ) => (
								<Dashicon
									key={ `nab-${ i }` }
									icon="star-filled"
								/>
							)
						) }
						{ range( getStars( candidate.priority ).halfStars ).map(
							( i ) => (
								<Dashicon
									key={ `nab-${ i }` }
									icon="star-half"
								/>
							)
						) }
						{ range(
							getStars( candidate.priority ).emptyStars
						).map( ( i ) => (
							<Dashicon key={ `nab-${ i }` } icon="star-empty" />
						) ) }
					</div>
					<p className="nab-ai-test-generation-modal__candidate-name">
						{ 'name' in candidate
							? candidate.name
							: candidate.title }
					</p>
					<p className="nab-ai-test-generation-modal__candidate-rationale">
						{ candidate.rationale }
					</p>
				</div>
			</div>
			<CandidateHypotheses candidate={ candidate } update={ update } />
		</div>
	);
};

const CandidateHypotheses = ( {
	candidate,
	update,
}: {
	readonly candidate: AiTestCandidate;
	readonly update: ( c: AiTestCandidate ) => void;
} ): JSX.Element => {
	const { generateTestHypotheses } = useDispatch( NAB_DATA );
	const types = useExperimentTypes();

	const hypothesisState = candidate.hypothesisState;
	if ( 'error' === hypothesisState.status ) {
		return (
			<div className="nab-ai-test-generation-modal__candidate-hypotheses">
				<p className="nab-ai-test-generation-modal__candidate-hypotheses-error">
					{ getErrorMessage( hypothesisState.reason ) }
				</p>
			</div>
		);
	}

	if ( 'subscription-required' === candidate.hypothesisGeneration ) {
		return (
			<div className="nab-ai-test-generation-modal__candidate-hypotheses-actions">
				<PremiumFeature feature="view-test-hypotheses" />
			</div>
		);
	}

	if ( 'ready' !== hypothesisState.status || ! hypothesisState.isVisible ) {
		return (
			<div className="nab-ai-test-generation-modal__candidate-hypotheses-actions">
				<Button
					variant="secondary"
					isBusy={ 'generating' === hypothesisState.status }
					disabled={ 'generating' === hypothesisState.status }
					onClick={ () => generateTestHypotheses( candidate, types ) }
				>
					{ 'pending' === hypothesisState.status ||
					'ready' === hypothesisState.status
						? _x(
								'View Testing Ideas',
								'command',
								'nelio-ab-testing'
						  )
						: _x(
								'Loading Testing Ideas…',
								'text',
								'nelio-ab-testing'
						  ) }
				</Button>
			</div>
		);
	}

	return (
		<div className="nab-ai-test-generation-modal__candidate-hypotheses">
			{ hypothesisState.hypotheses.map( ( hypothesis, index ) => (
				<Hypothesis
					key={ `nab-hypothesis-${ index }` }
					hypothesis={ hypothesis }
				/>
			) ) }
			<Button
				className="nab-ai-test-generation-modal__candidate-hide-testing-ideas-button"
				variant="link"
				onClick={ () =>
					update( {
						...candidate,
						hypothesisState: {
							...hypothesisState,
							isVisible: false,
						},
					} )
				}
			>
				{ _x( 'Hide Testing Ideas', 'command', 'nelio-ab-testing' ) }
			</Button>
		</div>
	);
};

const Hypothesis = ( {
	hypothesis,
}: {
	readonly key: string;
	readonly hypothesis: AiTestHypothesis;
} ) => {
	const [ testType, setTestType ] = useState( hypothesis.testType );
	const types = useExperimentTypes();
	const { createTestFromHypothesis } = useDispatch( NAB_DATA );
	return (
		<div
			className={ clsx( {
				'nab-ai-test-generation-modal__hypothesis': true,
				'nab-ai-test-generation-modal__hypothesis--is-used':
					!! hypothesis.relatedTests.length,
			} ) }
		>
			<p className="nab-ai-test-generation-modal__hypothesis-name">
				<span>{ hypothesis.name }</span>
			</p>
			<div className="nab-ai-test-generation-modal__hypothesis-block">
				<p>{ hypothesis.description }</p>
			</div>
			<div className="nab-ai-test-generation-modal__hypothesis-block">
				<Dashicon icon="admin-tools" />
				<strong>
					{ _x( 'Actionable', 'text', 'nelio-ab-testing' ) }
				</strong>
				<p>{ hypothesis.details.actionable }</p>
			</div>
			<div className="nab-ai-test-generation-modal__hypothesis-block">
				<Dashicon icon="welcome-learn-more" />
				<strong>
					{ _x( 'Rationale', 'text', 'nelio-ab-testing' ) }
				</strong>
				<p>{ hypothesis.details.rationale }</p>
			</div>
			<div className="nab-ai-test-generation-modal__hypothesis-actions">
				<TestTypeSelector
					baseType={ hypothesis.testType }
					currentType={ testType }
					setType={ setTestType }
				/>
				<Button
					variant="secondary"
					onClick={ () =>
						createTestFromHypothesis(
							{ ...hypothesis, testType },
							types
						)
					}
				>
					{ !! hypothesis.relatedTests.length
						? _x( 'Recreate Test', 'command', 'nelio-ab-testing' )
						: _x( 'Create Test', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</div>
	);
};

const TestTypeSelector = ( {
	baseType,
	currentType,
	setType,
}: {
	readonly baseType: string;
	readonly currentType: string;
	readonly setType: ( typeName: string ) => void;
} ) => {
	const compatiblePostTests = [
		'nab/css',
		'nab/javascript',
		'nab/php',
		'nab/synced-pattern',
	];
	const compatibleTestsMap: Record< string, string[] > = {
		'nab/page': compatiblePostTests,
		'nab/post': [ ...compatiblePostTests, 'nab/headline' ],
		'nab/custom-post-type': compatiblePostTests,
		'nab/wc-product': [ 'nab/css', 'nab/javascript', 'nab/php' ],
	};

	const compatibleTestNames = [
		baseType,
		...( compatibleTestsMap[ baseType ] ?? [] ),
	];
	const testTypes = useSelect(
		( select ) =>
			Object.values( select( NAB_EXPERIMENTS ).getExperimentTypes() ),
		[]
	);

	const compatibleTestTypes = testTypes.filter( ( t ) =>
		compatibleTestNames.includes( t.name )
	);
	if ( compatibleTestTypes.length < 2 ) {
		return null;
	}

	return (
		<SelectControl
			options={ orderBy(
				compatibleTestTypes.map( ( t ) => ( {
					label: t.title,
					value: t.name,
				} ) ),
				'label'
			) }
			value={ currentType }
			onChange={ setType }
		/>
	);
};

const DoneContent = ( {
	state,
	isLoadingPage,
	markAsLoading,
}: LoadingPageProps & {
	readonly state: AiTestGenerationDoneState;
} ) => {
	return (
		<div className="nab-ai-test-generation-modal__done">
			<div className="nab-ai-test-generation-modal__done-title">
				{ _x(
					'Your A/B Test Is Almost Ready!',
					'text',
					'nelio-ab-testing'
				) }
			</div>
			<Confetti className="nab-ai-test-generation-modal__done-icon" />
			<div className="nab-ai-test-generation-modal__done-description">
				{ _x(
					'Nelio AI has automatically generated a new test template for you. Take a moment to review the test details and ensure everything is correctly configured, and launch the test to start optimizing your website performance.',
					'text',
					'nelio-ab-testing'
				) }
			</div>
			<div className="nab-ai-test-generation-modal__done-actions">
				<Button
					disabled={ isLoadingPage }
					variant="secondary"
					onClick={ () => {
						markAsLoading();
						window.location.reload();
					} }
				>
					{ _x( 'Close', 'command', 'nelio-ab-testing' ) }
				</Button>
				<Button
					disabled={ isLoadingPage }
					variant="primary"
					onClick={ () => {
						markAsLoading();
						window.location.href = state.experiment.links.edit;
					} }
				>
					{ _x( 'Edit Test', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</div>
	);
};

const ErrorContent = ( { message }: { readonly message?: string } ) => {
	const { setPageAttribute } = useDispatch( NAB_DATA );
	return (
		<div className="nab-ai-test-generation-modal__error">
			<p>{ message || getErrorMessage() }</p>
			<Button
				variant="secondary"
				onClick={ () =>
					setPageAttribute( 'ai/testGenerationState', undefined )
				}
			>
				{ _x( 'Close', 'command', 'nelio-ab-testing' ) }
			</Button>
		</div>
	);
};

// =====
// HOOKS
// =====

const useTypeNameGetter = () => {
	const postTypes = useSelect(
		( select ) => select( NAB_DATA ).getKindEntities(),
		[]
	);
	return ( type: string ): string =>
		postTypes.find( ( pt ) => pt.name === type )?.labels.singular_name ??
		capitalize( type );
};

const useExperimentTypes = () =>
	useSelect(
		( select ) =>
			Object.values( select( NAB_EXPERIMENTS ).getExperimentTypes() ),
		[]
	);

// =======
// HELPERS
// =======

function getErrorMessage( reason?: string ): string {
	if ( ! reason ) {
		return _x(
			'Something went wrong. Please try again later.',
			'text',
			'nelio-ab-testing'
		);
	}

	return sprintf(
		'%1$s %2$s',
		sprintf(
			/* translators: %s: Error message/code. */
			_x( 'Something went wrong: %s.', 'text', 'nelio-ab-testing' ),
			reason
		),
		_x( 'Please try again later.', 'user', 'nelio-ab-testing' )
	);
}

function getStars( priority: number ) {
	priority = Math.min( Math.max( 0, priority ), 5 );
	priority = Math.round( priority * 2 );
	const isEven = 0 === priority % 2;
	const halfStars = isEven ? 0 : 1;
	priority = isEven ? priority / 2 : ( priority - 1 ) / 2;

	const fullStars = priority;
	const emptyStars = Math.max( 0, 5 - fullStars - halfStars );
	return { halfStars, fullStars, emptyStars };
}
